import { Identity } from '@/containers/Identities/types';
import classNames from 'classnames';
import React, { useMemo } from 'react';

export interface IdentitySelectorProps {
  /**
   * Array of available identities
   */
  identities: Identity[] | null | undefined;

  /**
   * Currently selected identity ID
   */
  value?: string;

  /**
   * Callback when identity selection changes
   */
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;

  /**
   * Placeholder text for the default option
   */
  placeholder?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Name attribute for the select element
   */
  name?: string;

  /**
   * The ID of the currently selected account (used for secondary sorting)
   */
  currentAccountId?: string | null;

  /**
   * The ID of the default identity for the current account (primary sorting key)
   */
  defaultIdentityId?: string;

  /**
   * Blur handler
   */
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
}

/**
 * A shared identity selector component that automatically sorts the current account's identity to the top.
 */
export const IdentitySelector: React.FC<IdentitySelectorProps> = ({
  identities,
  value,
  onChange,
  placeholder = 'Pick an identity',
  className,
  disabled = false,
  name = 'identity_id',
  currentAccountId,
  defaultIdentityId,
  onBlur,
}) => {
  const sortedIdentities = useMemo(() => {
    if (!identities) return [];
    if (!currentAccountId && !defaultIdentityId) return identities;

    return [...identities].sort((a, b) => {
      // 1. Priority: Default Identity ID
      if (defaultIdentityId) {
        if (a.identity_id === defaultIdentityId && b.identity_id !== defaultIdentityId)
          return -1;
        if (b.identity_id === defaultIdentityId && a.identity_id !== defaultIdentityId)
          return 1;
      }

      // 2. Priority: Matches currentAccountId (via account_id, base_account_id, or identity_id)
      if (currentAccountId) {
        const isCurrent = (identity: typeof a) =>
          String(identity.account_id) === String(currentAccountId) ||
          String(identity.base_account_id) === String(currentAccountId) ||
          String(identity.identity_id) === String(currentAccountId);

        const aIsCurrent = isCurrent(a);
        const bIsCurrent = isCurrent(b);

        if (aIsCurrent && !bIsCurrent) return -1;
        if (bIsCurrent && !aIsCurrent) return 1;
      }

      return 0;
    });
  }, [identities, currentAccountId, defaultIdentityId]);

  return (
    <select
      name={name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      className={classNames(
        'focus:input-primary active:input-primary select select-bordered w-full text-sm',
        className
      )}>
      {/* Default/placeholder option */}
      <option value="">{placeholder}</option>

      {/* Identity options */}
      {sortedIdentities.map((identity, idx) => (
        <option key={identity.identity_id || idx} value={identity.identity_id}>
          {identity.identity_name}
        </option>
      ))}
    </select>
  );
};
